import { useState } from "react";
import Image from "next/image";
import { useTranslation } from "next-i18next";
import { useAppContext } from "@/src/config/AppProvider";
import { SubmitHandler, useForm } from "react-hook-form";
import { ContactType } from "@/src/utils/enum";
import { Contact } from "@/lib/types/Contact";
import Api from "@/lib/api";
import styled from "styled-components";

interface ContactFormValues {
  name: string;
  email: string;
  phoneNumber: string;
  contactType: ContactType;
  subject: string;
  message: string;
}

const ContactFormSection = () => {
  const { t } = useTranslation();
  const { getLocalization } = useAppContext();
  const [success, setSuccess] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ContactFormValues>({
    defaultValues: {
      contactType: ContactType.None
    }
  });

  const onSubmit: SubmitHandler<ContactFormValues> = async data => {
    setIsSubmitting(true);

    try {
      await Api.Client.post<Contact, Contact>("/api/contact", {
        FullName: data.name,
        PhoneNumber: data.phoneNumber,
        EmailAddress: data.email,
        ContactType: Number(data.contactType),
        Subject: data.subject,
        Message: data.message
      });

      setSuccess(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`rbt-contact-address rbt-section-gap2`}>
      <div className="container">
        <div className="row g-5">
          <div className="col-lg-6">
            <div className="thumbnail">
              <Image className="w-100 radius-6" src={getLocalization("contactImage")} width={667} height={661} alt={t("contactWithUs")} />
            </div>
          </div>
          <div className="col-lg-6">
            <div className="rbt-contact-form contact-form-style-1 max-width-auto h-100">
              <div className="section-title text-start">
                <span className="subtitle bg-primary-opacity">{t("contactFormTitle")}</span>
              </div>
              <h3 className="title">{t("contactFormDescription")}</h3>

              <form id="contact-form" onSubmit={handleSubmit(onSubmit)} className="rainbow-dynamic-form max-width-auto">
                <div className="form-group">
                  <input
                    {...register("name", { required: true })}
                    id="contact-name"
                    type="text"
                    placeholder={t("contactForm.name")}
                    className={errors.name ? "border-danger" : ""}
                  />
                </div>
                <div className="d-flex justify-content-between gap-5">
                  <div className="form-group w-100">
                    <input
                      {...register("email", {
                        required: true,
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: t("invalidEmail")
                        }
                      })}
                      type="email"
                      placeholder={t("contactForm.email")}
                      className={errors.email ? "border-danger" : ""}
                    />
                    {errors.email && <span className="color-danger fs-5">{errors.email.message}</span>}
                  </div>
                  <div className="form-group w-100">
                    <input
                      {...register("phoneNumber", {
                        required: true
                      })}
                      type="text"
                      placeholder={t("contactForm.phoneNumber")}
                      className={errors.email ? "border-danger" : ""}
                    />
                    {errors.email && <span className="color-danger fs-5">{errors.email.message}</span>}
                  </div>
                </div>
                <div className="form-group">
                  <StyledSelect className="contact-select" {...register("contactType")}>
                    <option value={ContactType.None}>{t("contactForm.type")}</option>
                    <option value={ContactType.Callback}>{t("contactForm.types.callback")}</option>
                    <option value={ContactType.Registration}>{t("contactForm.types.registration")}</option>
                    <option value={ContactType.SuggestionRequest}>{t("contactForm.types.suggestionRequest")}</option>
                    <option value={ContactType.Complaint}>{t("contactForm.types.complaint")}</option>
                    <option value={ContactType.JobApplication}>{t("contactForm.types.jobApplication")}</option>
                  </StyledSelect>
                </div>
                <div className="form-group">
                  <input
                    {...register("subject", { required: true })}
                    type="text"
                    id="subject"
                    placeholder={t("contactForm.subject")}
                    className={errors.subject ? "border-danger" : ""}
                  />
                </div>
                <div className="form-group">
                  <textarea
                    {...register("message", { required: true })}
                    id="contact-message"
                    placeholder={t("contactForm.message")}
                    className={errors.message ? "border-danger" : ""}
                  ></textarea>
                </div>
                {Object.keys(errors).length > 0 && <div className="my-5 color-danger">{t("contactForm.pleaseFillAllFields")}</div>}
                <div className="form-submit-group">
                  {success ? (
                    <div className="alert alert-success d-flex align-items-center gap-3">
                      <i className="fa feather-check-circle"></i>
                      {t("contactForm.success")}
                    </div>
                  ) : (
                    <button
                      name="submit"
                      type="submit"
                      id="submit"
                      className="rbt-btn btn-md btn-gradient hover-icon-reverse w-100"
                      disabled={isSubmitting}
                    >
                      <span className="icon-reverse-wrapper">
                        <span className="btn-text">{t("contactForm.send")}</span>
                        <span className="btn-icon">
                          <i className="feather-arrow-right"></i>
                        </span>
                        <span className="btn-icon">
                          <i className="feather-arrow-right"></i>
                        </span>
                      </span>
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StyledSelect = styled.select`
  color: var(--color-body);
  background-color: transparent;
  border-radius: 0;
  border-top: none;
  border-right: none;
  border-left: none;
  padding: 20px 0 8px 0;
  border-bottom: 2px solid var(--color-border);
  background-size: 12px;
`;
export default ContactFormSection;
